import type { Router } from "vue-router"
import type { Rogue } from "./sisuTypes"

const regionNames = new Map<string, Intl.DisplayNames>()

/**
 * The country a flag image shows, from the value its filename is built from (e.g. "United States",
 * "US.svg", "cms/rogue challenge/flags/CA.png"). Two-letter codes become the country's name in `locale`.
 */
export function flagLabel(value?: string | null, locale = 'en'): string {
  const name = (value || '').split('/').pop()!.replace(/\.(svg|png|jpe?g|webp)$/i, '').replace(/[_-]+/g, ' ').trim()

  if (/^[a-z]{2}$/i.test(name)) {
    try {
      if (!regionNames.has(locale)) {
        regionNames.set(locale, new Intl.DisplayNames([locale], { type: 'region' }))
      }

      return regionNames.get(locale)!.of(name.toUpperCase()) || name
    } catch {
      return name
    }
  }

  return name
}

/**
 * Alt text for a country flag image, e.g. "Canada flag", translated and named in the site's locale.
 * Without `rogue` (e.g. in isolated component tests) it falls back to English.
 */
export function flagAlt(value?: string | null, rogue?: Pick<Rogue, '__' | 'l10n'>): string {
  const country = flagLabel(value, rogue?.l10n?.locale)
  if (!country) {
    return ''
  }

  return rogue ? rogue.__('{country} flag', { country }) : `${country} flag`
}

export function getCloudinaryUrl(source?: string, width?: number, height?: number, background: string | null = '#f8f8f8', additionalTransforms: string[] = []): string | undefined {
  if (!source || typeof source !== 'string') {
    return undefined
  }

  source = source
    .replace('https://www.roguefitness.com/media/rogue/sli/', '/media/catalog/')
    .replace('/media/catalog/product/catalog', '/catalog')

  // Temp fix for M1 style images...
  if (source.match(/^\/.\/.\/[^/]*.(jpg|png)$/)) {
    source = `/media/catalog/product${source}`
  }

  if (source.indexOf('/svc_spa') === 0) {
    return source
  }

  if (source.match(/^(https?:)?\/\/assets\.roguefitness\.com/)) {
    source = source.replace(/(https?:)?\/\/assets\.roguefitness\.com/, '')
    // Replace preexisting cloudinary transforms
    if (source.match(/\/[^/]*f_auto/)) {
      source = source.replace(/\/[^/]+/, '')
    }
  }

  if (source.indexOf('/c/l/cld_') === 0) {
    // Hotfix for broken images
    source = `/media/catalog/product${source}`
  }

  if (source.indexOf('http') !== 0 && source.indexOf('/') !== 0) {
    source = `/${source}`
  }

  // Curalate CDN supports resizing via the /sc/{w}x{h} path segment.
  // Images can be found at /theindex/ryourogue and some product pages.
  if (/^https?:\/\/edge\.curalate\.com\//.test(source) && width) {
    return source.replace(/\/sc\/\d+x\d+/, `/sc/${width}x${width}`)
  }

  if (source.indexOf('/') !== 0 || source.indexOf('//') === 0) {
    return source
  }

  // Cloudinary reads the first /v<digits>/ segment as the asset version and parses every
  // segment before it as a transformation, so public IDs containing a folder like `v2`
  // 400 with "Invalid transformation component". Strip any delivery prefix (re-added via
  // `base`) and always emit one explicit version segment to terminate the transformations.
  source = source.replace(/^\/(?:image|video)\/upload\//, '/')

  const versionMatch = source.match(/^\/(v\d+)\//)
  const version = versionMatch ? versionMatch[1] : 'v1'
  if (versionMatch) {
    source = source.slice(versionMatch[0].length - 1)
  }

  const videoExtensions = ['.mp4', '.mov', '.avi', '.mkv', '.wmv', '.flv', '.webm']
  const isVideo = videoExtensions.some((ext) => source.toLowerCase().endsWith(ext))
  
  const isPdf = source.toLowerCase().endsWith('.pdf') || source.toLowerCase().includes('.pdf')

  const transform: string[] = []
  if (source.indexOf('.svg') === -1) {
    if (isPdf) {
      transform.push('pg_1')
    }
    
    transform.push('f_auto')
    transform.push('q_auto')

    const resizeWidth = width
    const resizeHeight = height

    if (isVideo) {
      transform.push('vc_auto')
      transform.push('ac_none')
    } else if (resizeWidth && resizeHeight) {
      if (!additionalTransforms.some((t) => t.indexOf('c_') === 0)) {
        transform.push(isPdf ? 'c_fit' : 'c_fill')
      }
      if (!isPdf && !additionalTransforms.some((t) => t.indexOf('g_') === 0)) {
        transform.push('g_center')
      }
    } else if (resizeHeight || resizeWidth) {
      if (!additionalTransforms.some((t) => t.indexOf('c_') === 0)) {
        transform.push('c_limit')
      }
    }
    if (resizeWidth) {
      transform.push(`w_${resizeWidth}`)
    }
    if (resizeHeight) {
      transform.push(`h_${resizeHeight}`)
    }

    if (background && background !== 'transparent') {
      transform.push(`b_rgb:${background.replace('#', '')}`)
    }

    if (additionalTransforms && additionalTransforms.length) {
      transform.push(...additionalTransforms)
    }
  }

  const base = `https://assets.roguefitness.com/${isVideo ? 'video/upload/' : isPdf ? 'image/upload/' : ''}`
  const path = [transform.join(','), version].filter(Boolean).join('/')

  if (source.indexOf(' ') !== -1) {
    return encodeURI(`${base}${path}${source}`)
  }

  return `${base}${path}${source}`
}

// Known Cloudinary transformation parameter prefixes (crop, dimensions, gravity, quality,
// format, effects, overlays).
const CLOUDINARY_TRANSFORM_KEYS = 'c|w|h|g|q|f|e|a|r|o|b|bo|co|ar'
// Matches one or more comma-joined `key_value` pairs using those prefixes, e.g.
// `c_fill,w_800,h_600,g_center` or `f_auto`.
const CLOUDINARY_TRANSFORM_SEGMENT = new RegExp(`^(?:${CLOUDINARY_TRANSFORM_KEYS})_[^,/]+(?:,(?:${CLOUDINARY_TRANSFORM_KEYS})_[^,/]+)*$`, 'i')

const HEADING = /<(h[1-6])\b[^>]*>([\s\S]*?)<\/\1>/gi
const HEADING_MEDIA = /<(img|svg|video|iframe|picture)\b/i

/**
 * Removes headings with no text or media from CMS HTML. Rich-text editors leave them behind
 * (e.g. a store-only span removed from inside an <h4>), and screen readers announce them as blank.
 * String-based so it also runs during SSR.
 */
export function removeEmptyHeadings(html: string): string {
  if (!html) return ''

  return html.replace(HEADING, (match, _tag, inner: string) => {
    if (HEADING_MEDIA.test(inner)) return match
    const text = inner.replace(/<[^>]*>/g, '').replace(/&nbsp;|&#160;|&#xa0;/gi, ' ')

    return text.trim() ? match : ''
  })
}

export function optimizeCmsImages(html: string): string {
  if (!html) return ''

  return html.replace(
    /<img([^>]*?)src="(https?:\/\/assets\.roguefitness\.com\/[^"]+)"([^>]*?)>/gi,
    (_match, before, src, after) => {
      const uploadMatch = src.match(/\/image\/upload\/([^/]+)\//)

      if (uploadMatch && CLOUDINARY_TRANSFORM_SEGMENT.test(uploadMatch[1])) {
        return _match
      }

      const widthMatch = (before + after).match(/\bwidth="(\d+)/)
      const w = widthMatch ? parseInt(widthMatch[1]) : undefined
      const transforms = w ? `f_auto,q_auto,c_limit,w_${w}` : 'f_auto,q_auto'
      // Keep the asset's own version if it has one, otherwise add v1 — without a version
      // segment Cloudinary parses a `v2`-style folder as the version and 400s.
      const optimizedSrc = /\/image\/upload\//.test(src)
        ? src.replace(/\/image\/upload\/(v\d+\/)?/, (_m: string, v?: string) => `/image/upload/${transforms}/${v ?? 'v1/'}`)
        : (getCloudinaryUrl(src, w, undefined, null) ?? src)

      return `<img${before}src="${optimizedSrc}"${after}>`
    }
  )
}

export function redirect(to: string, router: Router, rogue: Rogue): void {
  if (!to) {
    return
  }

  if (router && to.indexOf('http') !== 0) {
    router.push(to)

    return
  }

  if (!rogue.appContext) {
    // @ts-expect-error window location type not set
    window.location = to
  } else {
    rogue.emitToApp?.('linkclick', { href: to })
  }
}
